import type { documentation } from '@cwos/api-client';
import type { Severity } from '@cwos/ui-shared';

/**
 * Pure presentation logic for the documentation COMPLETENESS surface — the
 * metadata half of ruling 7, which reached Intelligence and Agents and not the
 * supervisor whose job it is.
 *
 * ============================================================================
 * THIS FILE IS ABOUT STATUS. IT IS NOT ABOUT NOTES.
 * ============================================================================
 * The founder's boundary: Phibian may know documentation STATUS and must never
 * become where the document is authored. `GET /documentation/completeness`
 * respects it — a projection with no narrative field, `assertMetadataOnly` over
 * the whole envelope, and a test over the projection TEXT so a `se.note` added
 * to the SELECT is visible even though `client.query<Row>` would hide it from
 * `tsc`. The screen has to respect it too, and the screen is where the guard
 * runs out: nothing on the server can see a client that renders a field the
 * server never sent, or that links "read the note" out of a status list.
 *
 * So there is no label here for note content, no excerpt, no "preview", and no
 * derived reading of a narrative. Every function below turns a STATE, a COUNT
 * or a TIMESTAMP into words. `documentation-completeness-line.test.ts` asserts
 * that over this file's source text, in the same spirit as the server's own
 * line test — a word-family check, because the narrative arrives under a new
 * name every time somebody adds a convenience.
 *
 * Kept free of React so the mapping is pinned by unit tests, exactly as
 * `documentationLogic.ts` beside it is.
 */

export type Completeness = documentation.DocumentationCompleteness;
export type Lateness = documentation.DocumentationLateness;
export type DueBasis = documentation.DocumentationDueBasis;
export type MetadataRecord = documentation.DocumentationMetadataRecord;

/**
 * The five completeness values, in the order a reader should meet them: the
 * work still owed first, the work that is done after it, and the two that mean
 * "this row is not a live obligation" last.
 */
export const COMPLETENESS_VALUES: Completeness[] = [
  'missing',
  'in_progress',
  'unspecified',
  'complete',
  'not_owed',
];

/**
 * WHAT EACH VALUE MEANS TO A PERSON, and `unspecified` is the one that earns
 * its own sentence rather than being folded into "not started".
 *
 * `unspecified` is the server's answer for `state = 'late'`, which says the
 * record is open and late and says NOTHING about whether anything is written —
 * the table represents lateness twice, as a value of `state` AND as `is_late`,
 * with no constraint tying them (`metadata.ts:COMPLETENESS_OF`). Labelling it
 * "Not started" would be this screen guessing on the schema's behalf, and
 * guessing in the direction that makes a half-written note look untouched.
 */
export function completenessLabel(value: Completeness): string {
  switch (value) {
    case 'missing':
      return 'Not started';
    case 'in_progress':
      return 'In progress';
    case 'complete':
      return 'Complete';
    case 'not_owed':
      return 'Not owed';
    case 'unspecified':
      return 'Open — extent unknown';
  }
}

export function completenessPillVariant(value: Completeness): Severity {
  switch (value) {
    case 'complete':
      return 'good';
    case 'in_progress':
      return 'sky';
    case 'missing':
      return 'unsure';
    case 'unspecified':
      return 'unsure';
    case 'not_owed':
      return 'plain';
  }
}

/**
 * Completeness values that mean the write-up is still OUTSTANDING.
 *
 * DERIVED FROM THE SERVER'S OWN SET, not retyped as a wish.
 * `agent-runtime/productionAdapters.ts:OUTSTANDING_COMPLETENESS` is
 * `{missing, in_progress, unspecified}` — the previous `OUTSTANDING_STATES` set
 * translated key by key through `COMPLETENESS_OF`, so the population cannot
 * drift by a renaming. `documentation-completeness-logic.test.ts` parses that
 * literal out of the server file and asserts this set equals it, so the model's
 * idea of "outstanding" and the supervisor's cannot come apart.
 *
 * `unspecified` IS OUTSTANDING. It is the `late` state, which is open by
 * definition; excluding it because its extent is unknown would silently shrink
 * every outstanding count on this screen.
 */
export const OUTSTANDING: ReadonlySet<Completeness> = new Set<Completeness>([
  'missing',
  'in_progress',
  'unspecified',
]);

export function isOutstanding(value: Completeness): boolean {
  return OUTSTANDING.has(value);
}

/**
 * WHAT A CELL SAYS WHEN LATENESS COULD NOT BE MEASURED AT ALL.
 *
 * `DocumentationLateness` has four arms and `undeterminable` is the one this
 * product has collapsed before. It means the organization has declared no due
 * window, so lateness is WITHHELD for want of policy: it is not "on time", it
 * is not "0 days late", and it is not an empty cell. An empty cell is the worst
 * of the three, because a reader scanning a column of "—" concludes nothing is
 * late.
 *
 * A constant rather than a `latenessLabel(lateness)` switch, because this
 * screen rolls records up by person and never renders one record's lateness on
 * its own. A label function with no caller would be inert carriage — a value
 * plumbed through and never read — and inert carriage is how a mapping rots
 * while its unit test keeps passing.
 */
export const NOT_MEASURABLE = 'Not measurable';

/**
 * WHY THE PAST-DUE FIGURE IS NOT A NUMBER, when it is not a number.
 *
 * `counts.pastDue` is `number | null` and the null arrives with
 * `dueBasis: 'undeclared'`. Both spellings say the same thing and the screen
 * needs the sentence, not the null: a supervisor looking at a blank tile has to
 * be told that nobody has said when documentation is due, and what to do about
 * it. Returning `null` for the declared case keeps the ordinary screen quiet.
 */
export function dueBasisNote(basis: DueBasis, hours: number | null): string | null {
  if (basis === 'org_rule') return null;
  if (basis === 'default') {
    return (
      `Measured against Phibian\u2019s default: documentation is due ${hours} hours after the visit, ` +
      'because your organization has not set its own window. An org admin can change it in the ' +
      'documentation rule.'
    );
  }
  return (
    'Nobody has declared when documentation is due here, so lateness cannot be measured — this ' +
    'is not a statement that nothing is late. An org admin can set the window in the ' +
    'documentation rule.'
  );
}

/**
 * The heading sentence for the whole surface, framed by whose numbers these are.
 *
 * A `switch` over the union rather than a ternary over a boolean, so a fourth
 * frame could not be added without the compiler stopping here — the same rule
 * `DocumentationQueue.blurb` follows, and for the same reason: the boolean it
 * replaced had no room for "we do not know yet" and printed the
 * organization-wide sentence on every cold load.
 */
export const STATUS_ONLY = 'Status only — this screen never shows what a note says.';

export function completenessBlurb(frame: 'organization' | 'narrowed' | 'unknown'): string {
  switch (frame) {
    case 'organization':
      return `Where documentation stands across your organization. ${STATUS_ONLY}`;
    case 'narrowed':
      return `Where documentation stands for the people you can see. ${STATUS_ONLY}`;
    case 'unknown':
      return `Where documentation stands for what your account can see. We could not check how far that reaches. ${STATUS_ONLY}`;
  }
}

/**
 * THE ZERO, AND WHOSE ZERO IT IS.
 *
 * An empty page of records over a real 200 means there is nothing owed in the
 * population the server counted — which is a different claim for each frame,
 * and the reassuring one is the one nobody checks.
 */
export function emptyMessage(frame: 'organization' | 'narrowed' | 'unknown'): string {
  switch (frame) {
    case 'organization':
      return 'No delivered visit in your organization owes documentation.';
    case 'narrowed':
      return 'No delivered visit among the people you can see owes documentation. Someone with organization-wide access may see more.';
    case 'unknown':
      return 'No delivered visit your account can see owes documentation. We could not check how far that reaches, so someone else may see more.';
  }
}

// ---------------------------------------------------------------------------
// The per-person rollup — Ops Console's `charting_open` tab, honestly framed
// ---------------------------------------------------------------------------

/**
 * One person's outstanding documentation, rolled up from the returned page.
 *
 * `workerId: null` IS A REAL ROW, not a bucket to drop: the obligations
 * attributed to NOBODY (`workerIds` empty — the server's `unattributed`). An
 * obligation nobody owns is precisely the one that never gets written —
 * dropping it would make the rollup quietly smaller than the count above it.
 *
 * A GROUP SESSION credits EACH attributed worker (the per-worker credit rule),
 * so the per-person rows can sum above the record count; it is never filed
 * under the null row, because several people owing a note is not nobody.
 *
 * `pastDue` is `number | null` for the same reason the summary's is: over rows
 * whose lateness is `undeterminable` there is no past-due count to give, and a
 * `0` there would read as "none of this person's work is late" when the truth
 * is that nobody has said when it is due.
 */
export interface WorkerRollup {
  workerId: string | null;
  /** Rows in the page for this person, whatever their completeness. */
  obligations: number;
  /** Rows whose completeness is in `OUTSTANDING`. */
  outstanding: number;
  /** Rows measured past their due instant, or null when none could be measured. */
  pastDue: number | null;
  /** Rows whose lateness could not be measured at all. */
  undeterminable: number;
  /** The oldest visit in the page for this person — the age a queue is read by. */
  oldestOccurredAt: string | null;
}

/**
 * ROLLED UP OVER THE PAGE, WHICH IS NOT THE POPULATION.
 *
 * `counts` comes from the server's own `summariseDocumentationCompleteness`
 * over the WHOLE scoped set; `records` is one page of at most 200. Deriving
 * per-person figures from the page is correct and it is a SMALLER population
 * than the tiles above, so the screen has to say so whenever `total` exceeds
 * the page. The documentation queue shipped exactly this mismatch — a server
 * `total` beside two counts computed over the capped list, with nothing saying
 * they described different sets — and it is the reason `pageCovers` below
 * exists rather than a comment.
 *
 * Sorted by outstanding work descending, then by the oldest visit, so the
 * person with the most and the longest-standing is first. Ties broken on a
 * stable key so the order does not shuffle between renders.
 */
export function rollupByWorker(records: readonly MetadataRecord[]): WorkerRollup[] {
  const byWorker = new Map<string, WorkerRollup>();
  // A group session credits EACH attributed worker; only `workerIds: []` is nobody.
  const credited = records.flatMap((record) =>
    (record.workerIds.length > 0 ? record.workerIds : [null]).map((workerId) => ({
      record,
      workerId,
    })),
  );
  for (const { record, workerId } of credited) {
    const key = workerId ?? ' unassigned';
    let row = byWorker.get(key);
    if (row === undefined) {
      row = {
        workerId,
        obligations: 0,
        outstanding: 0,
        pastDue: null,
        undeterminable: 0,
        oldestOccurredAt: null,
      };
      byWorker.set(key, row);
    }
    row.obligations += 1;
    if (isOutstanding(record.completeness)) row.outstanding += 1;
    if (record.lateness.kind === 'past_due') row.pastDue = (row.pastDue ?? 0) + 1;
    // A row that COULD be late and was measured as on-time still establishes
    // that this person's lateness is measurable, so the null stops being the
    // honest answer and 0 starts being one.
    if (record.lateness.kind === 'within_window' || record.lateness.kind === 'closed') {
      row.pastDue = row.pastDue ?? 0;
    }
    if (record.lateness.kind === 'undeterminable') row.undeterminable += 1;
    if (row.oldestOccurredAt === null || record.occurredAt < row.oldestOccurredAt) {
      row.oldestOccurredAt = record.occurredAt;
    }
  }
  return [...byWorker.values()].sort((a, b) => {
    if (b.outstanding !== a.outstanding) return b.outstanding - a.outstanding;
    const ao = a.oldestOccurredAt ?? '';
    const bo = b.oldestOccurredAt ?? '';
    if (ao !== bo) return ao < bo ? -1 : 1;
    return (a.workerId ?? '').localeCompare(b.workerId ?? '');
  });
}

/**
 * Whether the page the rollup was built from covers the whole counted set.
 *
 * `null` when `total` is not known — the response has not arrived, so neither
 * "it covers everything" nor "it does not" may be asserted.
 */
export function pageCovers(shown: number, total: number | null | undefined): boolean | null {
  if (total === null || total === undefined) return null;
  return shown >= total;
}
